import type {
	ConversionRateByDays,
	ChannelAnalytics,
	CategoryConversionMetrics,
} from '../types'

/**
 * Format conversion rate as percentage
 */
export function formatConversionRate(rate: number): string {
	return `${rate.toFixed(2)}%`
}

/**
 * Format amount as Thai currency
 */
export function formatConversionAmount(amount: number): string {
	return `฿${amount.toLocaleString('th-TH', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	})}`
}

/**
 * Format number with Thai locale
 */
export function formatNumber(value: number): string {
	return value.toLocaleString('th-TH')
}

/**
 * Get the best performing period from conversion data
 */
export function getBestPerformingPeriod(conversion: ConversionRateByDays): {
	period: string
	rate: number
} {
	const periods = [
		{ period: '1 วัน', rate: conversion.day1.rate, key: 'day1' },
		{ period: '3 วัน', rate: conversion.day3.rate, key: 'day3' },
		{ period: '5 วัน', rate: conversion.day5.rate, key: 'day5' },
		{ period: '7 วัน', rate: conversion.day7.rate, key: 'day7' },
		{ period: '14 วัน', rate: conversion.day14.rate, key: 'day14' },
		{ period: '30 วัน', rate: conversion.day30.rate, key: 'day30' },
	]

	const best = periods.reduce((prev, current) =>
		current.rate > prev.rate ? current : prev,
	)

	return { period: best.period, rate: best.rate }
}

/**
 * Reactivation category keys
 */
export type ReactivationCategory =
	| 'newDepositor'
	| 'inactive7d'
	| 'inactive14d'
	| 'inactive30d'
	| 'regularUser'

/**
 * Category labels in Thai (with day range clarification)
 */
export const CATEGORY_LABELS: Record<ReactivationCategory, string> = {
	newDepositor: 'ลูกค้าใหม่ (ไม่เคยฝาก)',
	inactive7d: 'หยุดฝาก 7-13 วัน',
	inactive14d: 'หยุดฝาก 14-29 วัน',
	inactive30d: 'หยุดฝาก 30+ วัน',
	regularUser: 'ลูกค้าประจำ',
}

/**
 * Category short labels in Thai
 */
export const CATEGORY_SHORT_LABELS: Record<ReactivationCategory, string> = {
	newDepositor: 'ลูกค้าใหม่',
	inactive7d: '7-13 วัน',
	inactive14d: '14-29 วัน',
	inactive30d: '30+ วัน',
	regularUser: 'ลูกค้าประจำ',
}

/**
 * Category descriptions for tooltips
 */
export const CATEGORY_DESCRIPTIONS: Record<ReactivationCategory, string> = {
	newDepositor: 'ผู้เล่นที่ยังไม่เคยฝากเงินมาก่อน',
	inactive7d: 'ผู้เล่นที่ไม่ได้ฝากเงินมา 7-13 วัน',
	inactive14d: 'ผู้เล่นที่ไม่ได้ฝากเงินมา 14-29 วัน',
	inactive30d: 'ผู้เล่นที่ไม่ได้ฝากเงินมา 30 วันขึ้นไป',
	regularUser: 'ผู้เล่นที่ฝากเงินภายใน 7 วันที่ผ่านมา',
}

/**
 * Category colors for charts and badges
 */
export const CATEGORY_COLORS: Record<ReactivationCategory, string> = {
	newDepositor: '#8b5cf6', // violet
	inactive7d: '#f59e0b', // amber
	inactive14d: '#f97316', // orange
	inactive30d: '#ef4444', // red
	regularUser: '#22c55e', // green
}

/**
 * Category background colors for UI
 */
export const CATEGORY_BG_COLORS: Record<ReactivationCategory, string> = {
	newDepositor:
		'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300',
	inactive7d:
		'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
	inactive14d:
		'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
	inactive30d: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
	regularUser:
		'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
}

/**
 * Get category data from CategoryConversionMetrics
 */
export interface CategoryData {
	key: ReactivationCategory
	label: string
	shortLabel: string
	recipients: number
	count: number
	amount: number
	color: string
	bgColor: string
	byDay: ConversionRateByDays
}

export function getCategoryData(
	metrics: CategoryConversionMetrics | undefined,
): CategoryData[] {
	if (!metrics) return []

	return [
		{
			key: 'newDepositor',
			label: CATEGORY_LABELS.newDepositor,
			shortLabel: CATEGORY_SHORT_LABELS.newDepositor,
			recipients: metrics.newDepositorRecipients,
			count: metrics.newDepositorCount,
			amount: metrics.newDepositorAmount,
			color: CATEGORY_COLORS.newDepositor,
			bgColor: CATEGORY_BG_COLORS.newDepositor,
			byDay: metrics.newDepositorByDay,
		},
		{
			key: 'inactive7d',
			label: CATEGORY_LABELS.inactive7d,
			shortLabel: CATEGORY_SHORT_LABELS.inactive7d,
			recipients: metrics.inactive7dRecipients,
			count: metrics.inactive7dCount,
			amount: metrics.inactive7dAmount,
			color: CATEGORY_COLORS.inactive7d,
			bgColor: CATEGORY_BG_COLORS.inactive7d,
			byDay: metrics.inactive7dByDay,
		},
		{
			key: 'inactive14d',
			label: CATEGORY_LABELS.inactive14d,
			shortLabel: CATEGORY_SHORT_LABELS.inactive14d,
			recipients: metrics.inactive14dRecipients,
			count: metrics.inactive14dCount,
			amount: metrics.inactive14dAmount,
			color: CATEGORY_COLORS.inactive14d,
			bgColor: CATEGORY_BG_COLORS.inactive14d,
			byDay: metrics.inactive14dByDay,
		},
		{
			key: 'inactive30d',
			label: CATEGORY_LABELS.inactive30d,
			shortLabel: CATEGORY_SHORT_LABELS.inactive30d,
			recipients: metrics.inactive30dRecipients,
			count: metrics.inactive30dCount,
			amount: metrics.inactive30dAmount,
			color: CATEGORY_COLORS.inactive30d,
			bgColor: CATEGORY_BG_COLORS.inactive30d,
			byDay: metrics.inactive30dByDay,
		},
		{
			key: 'regularUser',
			label: CATEGORY_LABELS.regularUser,
			shortLabel: CATEGORY_SHORT_LABELS.regularUser,
			recipients: metrics.regularUserRecipients,
			count: metrics.regularUserCount,
			amount: metrics.regularUserAmount,
			color: CATEGORY_COLORS.regularUser,
			bgColor: CATEGORY_BG_COLORS.regularUser,
			byDay: metrics.regularUserByDay,
		},
	]
}

/**
 * Get the best performing category based on conversion rate at day 7
 */
export function getBestPerformingCategory(
	metrics: CategoryConversionMetrics | undefined,
): CategoryData | null {
	const categories = getCategoryData(metrics)
	if (categories.length === 0) return null

	return categories.reduce((best, current) => {
		const bestRate = best.byDay.day7.rate
		const currentRate = current.byDay.day7.rate
		return currentRate > bestRate ? current : best
	})
}

/**
 * Transform analytics data for chart display (deposit/withdraw only, no play)
 */
export interface ChartData {
	period: string
	depositRate: number
	withdrawRate: number
}

export function getConversionTrendData(
	analytics: ChannelAnalytics | undefined,
): ChartData[] {
	if (!analytics) return []

	return [
		{
			period: '1 วัน',
			depositRate: analytics.depositConversion.day1.rate,
			withdrawRate: analytics.withdrawConversion.day1.rate,
		},
		{
			period: '3 วัน',
			depositRate: analytics.depositConversion.day3.rate,
			withdrawRate: analytics.withdrawConversion.day3.rate,
		},
		{
			period: '5 วัน',
			depositRate: analytics.depositConversion.day5.rate,
			withdrawRate: analytics.withdrawConversion.day5.rate,
		},
		{
			period: '7 วัน',
			depositRate: analytics.depositConversion.day7.rate,
			withdrawRate: analytics.withdrawConversion.day7.rate,
		},
		{
			period: '14 วัน',
			depositRate: analytics.depositConversion.day14.rate,
			withdrawRate: analytics.withdrawConversion.day14.rate,
		},
		{
			period: '30 วัน',
			depositRate: analytics.depositConversion.day30.rate,
			withdrawRate: analytics.withdrawConversion.day30.rate,
		},
	]
}

/**
 * Transform category data for trend chart (by day periods)
 */
export interface CategoryTrendData {
	period: string
	newDepositor: number
	inactive7d: number
	inactive14d: number
	inactive30d: number
	regularUser: number
}

export function getCategoryTrendData(
	metrics: CategoryConversionMetrics | undefined,
): CategoryTrendData[] {
	if (!metrics) return []

	const periods = ['day1', 'day3', 'day5', 'day7', 'day14', 'day30'] as const
	const periodLabels = {
		day1: '1 วัน',
		day3: '3 วัน',
		day5: '5 วัน',
		day7: '7 วัน',
		day14: '14 วัน',
		day30: '30 วัน',
	}

	return periods.map((period) => ({
		period: periodLabels[period],
		newDepositor: metrics.newDepositorByDay[period].rate,
		inactive7d: metrics.inactive7dByDay[period].rate,
		inactive14d: metrics.inactive14dByDay[period].rate,
		inactive30d: metrics.inactive30dByDay[period].rate,
		regularUser: metrics.regularUserByDay[period].rate,
	}))
}

/**
 * Get period label in Thai
 */
export function getPeriodLabel(period: keyof ConversionRateByDays): string {
	const labels: Record<keyof ConversionRateByDays, string> = {
		day1: '1 วัน',
		day3: '3 วัน',
		day5: '5 วัน',
		day7: '7 วัน',
		day14: '14 วัน',
		day30: '30 วัน',
	}
	return labels[period]
}

/**
 * Format message type for display
 */
export function formatMessageType(
	type: 'IMMEDIATE' | 'SCHEDULED' | 'AUTO' | 'INTERVAL',
): string {
	const labels: Record<string, string> = {
		IMMEDIATE: 'ทันที',
		SCHEDULED: 'ตามกำหนด',
		AUTO: 'อัตโนมัติ',
		INTERVAL: 'ตามช่วงเวลา',
	}
	return labels[type] || type
}

/**
 * Format channel for display
 */
export function formatChannel(channel: 'SMS' | 'LINE' | string): string {
	if (channel === 'SMS') return 'SMS'
	if (channel === 'LINE') return 'LINE'
	return channel
}

/**
 * Get message type badge color
 */
export function getMessageTypeBadgeColor(
	type: 'IMMEDIATE' | 'SCHEDULED' | 'AUTO' | 'INTERVAL',
): string {
	const colors: Record<string, string> = {
		IMMEDIATE: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
		SCHEDULED:
			'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
		AUTO: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
		INTERVAL:
			'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
	}
	return colors[type] || 'bg-gray-100 text-gray-800'
}

/**
 * Get channel badge color
 */
export function getChannelBadgeColor(channel: 'SMS' | 'LINE' | string): string {
	if (channel === 'SMS')
		return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300'
	if (channel === 'LINE')
		return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
	return 'bg-gray-100 text-gray-800'
}

/**
 * Calculate total reactivation conversions (excluding regular users)
 */
export function getTotalReactivationConversions(
	metrics: CategoryConversionMetrics | undefined,
): { count: number; amount: number } {
	if (!metrics) return { count: 0, amount: 0 }

	return {
		count:
			metrics.regularUserCount +
			metrics.newDepositorCount +
			metrics.inactive7dCount +
			metrics.inactive14dCount +
			metrics.inactive30dCount,
		amount:
			metrics.regularUserAmount +
			metrics.newDepositorAmount +
			metrics.inactive7dAmount +
			metrics.inactive14dAmount +
			metrics.inactive30dAmount,
	}
}
